import { NgModule } from '@angular/core';
import { RouterModule, RouteReuseStrategy, Routes, TitleStrategy } from '@angular/router';
import { GuestGuard } from './common/auth/guest.guard';
import { LoginGuard } from './common/auth/login.guard';
import { RedirectGuard } from './common/auth/redirect.guard';
import { AuthLayoutComponent } from './components/auth-layout/auth-layout.component';
import { SignUpTargets } from './enums/sign-up-targets.enum';
import { TrackingResolver } from './components/shared/tracking.resolver';
import { PayhoaTitleStrategy } from './common/title/payhoa-title.strategy';
import { GableReloadRouteReuseStrategy } from './services/gable/gable-reload-route-reuse.strategy';

const routes: Routes = [
    // root navigation will route to login
    { path: '', pathMatch: 'full', redirectTo: 'auth/login' },

    {
        path: 'sign-up',
        component: AuthLayoutComponent,
        canActivate: [GuestGuard],
        children: [
            {
                path: '',
                loadComponent: () => import('./components/auth-layout/sign-up/sign-up.component').then(m => m.SignUpComponent),
            },
            /** Targeted Roles for Signup */
            {
                path: SignUpTargets.HOMEOWNER,
                loadComponent: () => import('./components/auth-layout/sign-up/homeowner-sign-up/homeowner-sign-up.component').then(m => m.HomeownerSignUpComponent),
            },
            {
                path: SignUpTargets.HOMEOWNER + '/:org_url',
                loadComponent: () => import('./components/auth-layout/sign-up/homeowner-sign-up/homeowner-sign-up.component').then(m => m.HomeownerSignUpComponent),
            },
            {
                path: SignUpTargets.BOARD_MEMBER,
                loadComponent: () => import('./components/auth-layout/sign-up/board-sign-up/board-sign-up.component').then(m => m.BoardSignUpComponent),
            },
            {
                path: SignUpTargets.PROPERTY_MANAGER,
                loadComponent: () => import('./components/auth-layout/sign-up/pmc-sign-up/pmc-sign-up.component').then(m => m.PmcSignUpComponent),
            },
            /** Branded Organization for Signup */
            {
                path: ':org_url',
                loadComponent: () => import('./components/auth-layout/login/branded-login/branded-login.component').then(m => m.BrandedLoginComponent),
            },
        ],
        resolve: {
            tracking: TrackingResolver,
        },
    },

    // legacy join/* redirects to sign-up
    {path: 'join', redirectTo: '/sign-up', pathMatch: 'full'},
    {path: 'join/:org_url', redirectTo: '/sign-up/:org_url', pathMatch: 'full'},

    // Traditional auth-layout routes (login component handles authenticated users internally
    // with "Already Logged In...Redirecting You" interstitial — no guard needed)
    {
        path: 'auth',
        loadChildren: () => import('./components/auth-layout/auth-layout.module').then(m => m.AuthLayoutModule),
    },

    // V2 Layout routes for /app (must come BEFORE general /app route for proper matching)
    {
        path: 'app',
        loadChildren: () => import('./components/v2-layout/v2-layout.module.app').then(m => m.V2LayoutModuleApp),
    },
    // Traditional app-layout routes (catch-all for /app/*)
    {
        path: 'app',
        loadChildren: () => import('./components/app-layout/app-layout.module.payhoa').then(m => m.AppLayoutModulePayhoa),
    },

    // Traditional app-layout routes specific to events
    {
        path: 'events',
        loadChildren: () => import('./components/app-layout/app-layout.module').then(m => m.AppLayoutModule),
    },

    // V2 Layout routes for /admin (must come BEFORE general /admin route for proper matching)
    {
        path: 'admin',
        loadChildren: () => import('./components/v2-layout/v2-layout.module.admin').then(m => m.V2LayoutModuleAdmin),
    },
    // Traditional admin-layout routes (catch-all for /admin/*)
    {
        path: 'admin',
        loadChildren: () => import('./components/admin-layout/admin-layout.module').then(m => m.AdminLayoutModule),
    },

    // V2 Layout routes for /pmc
    {
        path: 'pmc',
        loadChildren: () => import('./components/v2-layout/v2-layout.module.pmc').then(m => m.V2LayoutModulePmc),
    },

    // The member ballot-casting page — STANDALONE and chrome-less, login-gated; legacy casting redirects here.
    {
        path: 'vote/:ballotId/member/:memberId',
        canActivate: [LoginGuard],
        loadComponent: () => import('./components/v2-layout/voting/user-voting-v2/user-voting-v2.component').then(m => m.UserVotingV2Component),
    },

    {
        path: 'redirect',
        loadComponent: () => import('./components/redirect/redirect.component').then(m => m.RedirectComponent),
    },

    {
        path: 'error', loadComponent: () => import('./components/error/error.component').then(m => m.ErrorComponent),
    },

    // marketing redirects
    { path: 'community-association-management-software', redirectTo: 'hoa-management-software' },
    { path: 'condo-management-software', redirectTo: 'hoa-management-software' },
    { path: 'hoa-coa-association-management-software-app', redirectTo: 'hoa-management-software' },
    { path: 'hoa-management-software-landing', redirectTo: 'hoa-management-software' },
    { path: 'hoa-management-software-screens', redirectTo: 'hoa-management-software' },
    {
        path: 'hoa-management-software',
        canActivate: [RedirectGuard],
        component: RedirectGuard,
        data: {
            externalUrl: 'https://www.payhoa.com/hoa-software-landing/',
        }
    },

    // catch any unmatched routes and redirect to the error path
    { path: '**', redirectTo: 'error' },
];

@NgModule({
    // onSameUrlNavigation:'reload' lets a navigate to the current URL re-run
    // the route (re-init the routed page component) — used by the Gable drawer
    // host to refresh the page behind the drawer after a write commits (SD-8019).
    imports: [RouterModule.forRoot(routes, { onSameUrlNavigation: 'reload' })],
    exports: [RouterModule],
    providers: [
        { provide: TitleStrategy, useClass: PayhoaTitleStrategy },
        // Default reuse behavior, plus a one-shot force-reload the Gable drawer
        // host uses to refresh the page behind the drawer after a write (SD-8019).
        GableReloadRouteReuseStrategy,
        { provide: RouteReuseStrategy, useExisting: GableReloadRouteReuseStrategy },
    ],
})
export class AppRoutingModule
{ }
